import { CATEGORY, COLOR, COLUMN, FORMATTING, JARGON, LEGEND } from "../../modules/constants"
import { addPaddingRightToLastCell, getCellWithTooltip, headCell } from "../../modules/table.funcs"
import { buildTooltipTitle, getAge, getMonYear, getStatusLevel } from "../../modules/utilities"

const { black, bold, dobMonYear, left, normal, right, string, yearsMonths } = FORMATTING

//#region header
const starHead = headCell({ id: COLUMN.STAR })

export const createSearchCompaniesHeaderData = ({ portrait, the350club }) => {
    const companyHead = headCell({ id: COLUMN.COMPANY, align: left, label: "Company" })
    const createdHead = headCell({ id: COLUMN.CREATED, label: JARGON.COMPANY_TIMELINE.FOUNDED })
    const ceasedHead = headCell({ id: COLUMN.CEASED, label: JARGON.COMPANY_TIMELINE.CEASED })
    const iconHead = headCell({ id: COLUMN.ICON })
    const locationHead = headCell({ id: COLUMN.LOCATION, label: "Location" })
    const numberHead = headCell({ id: COLUMN.NUMBER, label: "Number" })
    const statusHead = headCell({ id: COLUMN.STATUS, label: "Status" })
    const type = headCell({ id: COLUMN.COMPANY_TYPE, label: "Type" })
    const matchHead = headCell({ id: COLUMN.ORDER, align: right, label: "Match" })

    const cells = portrait
        ? [starHead, iconHead, companyHead, locationHead, matchHead]
        : [starHead, iconHead, companyHead, numberHead, statusHead, type, createdHead, ceasedHead, locationHead, matchHead]

    if (the350club)
        cells.shift() // Remove starHead

    addPaddingRightToLastCell(cells)

    return cells
}

export const createSearchOfficersHeaderData = ({ portrait, the350club }) => {
    const appointmentsHead = headCell({ id: COLUMN.APPOINTMENTS, label: JARGON.APPTS })
    const age = headCell({ id: COLUMN.AGE })
    const dobHead = headCell({ id: COLUMN.DOB })
    const iconHead = headCell({ id: COLUMN.ICON })
    const locationHead = headCell({ id: COLUMN.LOCATION })
    const officerHead = headCell({ id: COLUMN.OFFICER, align: left, label: "Name" })
    const starHead = headCell({ id: COLUMN.STAR })
    const matchHead = headCell({ id: COLUMN.ORDER, align: right })

    const cells = portrait
        ? [starHead, iconHead, officerHead, locationHead, matchHead]
        : [starHead, iconHead, officerHead, appointmentsHead, dobHead, age, locationHead, matchHead]

    if (the350club)
        cells.shift() // Remove starHead

    addPaddingRightToLastCell(cells)

    return cells
}
//#endregion header

//#region Rows
export const createCompaniesRowData = props => {
    // console.log('createCompaniesRowData props:', props)
    const { items, maxLseData, portrait } = props
    const rows = []

    items.forEach((item, index) => {
        item.category = CATEGORY.COMPANY

        const tooltipElements = portrait
            ? [
                item.description && `Reg. no. ${item.description}`,
                `status: ${item.companyStatus}`]
            : [""]

        const tooltip = buildTooltipTitle(tooltipElements)
        const statusColor = getStatusLevel(item.companyStatus)

        // The company name
        const name = getCellWithTooltip({
            tooltip,
            type: string,
            value: item.title,
        })

        // The company number
        const number = getCellWithTooltip({
            align: right,
            tooltip,
            type: string,
            value: item.companyNumber,
        })

        // Company status: 'active', 'dissolved' etc
        const status = getCellWithTooltip({
            align: right,
            color: COLOR[statusColor],
            tooltip,
            type: string,
            value: item.companyStatus,
        })

        // The date the company was founded
        const dateOfCreation = getCellWithTooltip({
            align: right,
            tooltip,
            type: yearsMonths,
            value: getMonYear(new Date(item.dateOfCreation)),
            sortValue: item.dateOfCreation,
        })

        // The date the company was dissolved - if any
        const dateOfCessation = getCellWithTooltip({
            align: right,
            tooltip,
            type: yearsMonths,
            value: item.dateOfCessation ? getMonYear(new Date(item.dateOfCessation)) : "n/a",
            sortValue: item.dateOfCessation ? item.dateOfCessation : "1000"
        })

        const address = getCellWithTooltip({
            align: right,
            tooltip,
            type: string,
            value: item.shortAddress,
        })

        // The order in which the original results were returned
        const match = getCellWithTooltip({
            align: right,
            tooltip,
            type: string,
            value: index + 1,
            sortValue: index,
        })

        // Company type: ltd, plc etc
        const type = getCellWithTooltip({
            align: right,
            tooltip,
            type: string,
            value: item.companyType
        })

        const cellWithTooltips = portrait
            ? [name, address, match]
            : [name, number, status, type, dateOfCreation, dateOfCessation, address, match]

        addPaddingRightToLastCell(cellWithTooltips)

        const row =
        {
            _id: item._id,
            category: item.category,
            cellWithTooltips,
            fontWeight: (maxLseData && item.lseCompany) ? bold : normal,
            icon: CATEGORY.COMPANY,
            iconColor: COLOR[statusColor],
            isFavourite: item.isFavourite ?? false,
            // Use the item.index rather than the 'forEach' index because the row index
            // must point to the original, rather than the filtered list item
            index: item.index,
            link: getLink(item),
            title: item.title,
        }

        rows.push(row)
    })

    return rows
}

export const createOfficersRowData = props => {
    // console.log('createOfficersRowData props:',props)
    const { items, portrait } = props
    const rows = []

    items.forEach((item, index) => {
        // The officer's name
        const name = getCellWithTooltip({
            type: string,
            value: item.name.toPresent,
            sortValue: item.name.toSort,
        })

        // The order in which the original results were returned
        const match = getCellWithTooltip({
            align: right,
            type: string,
            value: index + 1,
            sortValue: index,
        })

        const appointmentCount = getCellWithTooltip({
            align: right,
            type: string,
            value: item.appointmentCount,
        })

        const dateOfBirth = getCellWithTooltip({
            type: dobMonYear,
            value: item.dateOfBirth,
            sortValue: dateOfBirthSortValue(item.dateOfBirth)
        })

        const age = getCellWithTooltip({
            value: getAge(item.dateOfBirth),
            sortValue: dateOfBirthSortValue(item.dateOfBirth)
        })

        const address = getCellWithTooltip({
            align: right,
            type: string,
            value: item.shortAddress,
        })

        const cellWithTooltips = portrait
            ? [name, address, match] // portrait
            : [name, appointmentCount, dateOfBirth, age, address, match]

        addPaddingRightToLastCell(cellWithTooltips)

        const row =
        {
            _id: item._id,
            category: item.category,
            cellWithTooltips,
            icon: item.category,
            iconColor: black,
            // Use the item.index rather than the 'forEach' index because the row index
            // must point to the original, rather than the filtered list item
            index: item.index,
            isFavourite: item.isFavourite ?? false,
            link: getLink(item),
            title: item.title,
        }

        rows.push(row)
    })

    return rows
}

// If dateOfBirth not null, returns eg 196103. Else 0
const dateOfBirthSortValue = dateOfBirth => dateOfBirth
    ? dateOfBirth.year * 100 + dateOfBirth.month
    : 0

const appendDescription = item => {
    // Split eg "11947035 - Incorporated on 15 April 2019" to
    // ["1194703", "Incorporated on 15 April 2019"]
    const a = item.description ? item.description.split(" - ") : ""

    if (a.length > 1) {
        // Split eg "Incorporated on 15 April 2019" to
        // ["Incorporated", "15 April 2019"]
        const b = a[1].split(" on ")

        if (b.length > 1) {
            // Get dateOfCreation or dateOfCessation
            const dateStr = getReleventDateStr(b[0], item)

            if (dateStr)
                return `${b[0]} ${getMonYear(new Date(dateStr))}`
        }
    }
    // Return orginial description if above unsuccessful
    return item.description
}

const getReleventDateStr = (identifier, item) => {
    switch (identifier) {
        case 'Incorporated':
        case 'Registered':
        case 'Formed':
        case 'First UK establishment opened':
        case 'Opened':
            return item.dateOfCreation

        case 'Dissolved':
        case 'Converted/Closed':
        case 'Closed':
            return item.dateOfCessation

        default:
            /*
            'closed' : `Closed`
            'voluntary-arrangement': `Voluntary Arrangement`
            'receivership': `Receiver Action`
            'insolvency-proceedings' : `Insolvency Proceedings`
            'liquidation' : `Liquidation`
            'administration' : `In Administration`
            'registered-externally' : `Registered externally as {external_registration_number}`
            */
            return null
    }
}

const getLink = item => `/${item.category || item.keyValues["category"]}/${item.ids.join(',')}`
//#endregion Rows

